/*
 * The month overview's rules, apart from the page that draws them.
 *
 * The month is the week overview over more days. Which orders a day holds, which are akut, what
 * is carried and what can move are workOrderWeek.js's rules, given the month grid's days in place
 * of a week's. This file only says which days those are and how they add up.
 */
import { addWeeks, endOfMonth, isSameMonth, startOfISOWeek, startOfMonth } from "date-fns";
import {
  getEstimateTotal,
  getProjectedRangeRows,
  getRangeRows,
  getWeekDays,
  getWeekInfo,
  splitAcuteAndPlanned,
  splitRowsByDays,
} from "./workOrderWeek";

const DAYS_IN_WEEK = 7;

// The first day of the month the value falls in, at local midnight.
export const getMonthStart = (value = new Date()) => startOfMonth(value);

/*
 * The days a month view shows, in whole ISO weeks: from the Monday of the week the 1st falls in
 * to the Sunday of the week the last day falls in. That is four to six weeks, and the days at
 * either end may belong to the month before or after.
 *
 * As { monthStart, start, end, weeks, days }. The range is from start up to, not including, end,
 * the Monday after the last week. Each week is getWeekInfo's, with its seven days added. days is
 * every day of every week, in order.
 */
export const getMonthGrid = (month) => {
  const monthStart = startOfMonth(month);
  const start = startOfISOWeek(monthStart);
  const end = addWeeks(startOfISOWeek(endOfMonth(monthStart)), 1);

  const weeks = [];
  for (let weekStart = start; weekStart < end; weekStart = addWeeks(weekStart, 1)) {
    weeks.push({ ...getWeekInfo(weekStart), days: getWeekDays(weekStart) });
  }

  return { monthStart, start, end, weeks, days: weeks.flatMap((week) => week.days) };
};

/*
 * The orders the grid shows, by the week's rules over the grid's range. A grid that holds today
 * carries open work from before its first day, the same as the current week does.
 */
export const getMonthRows = (workOrders, grid, now = new Date()) =>
  getRangeRows(workOrders, grid.start, grid.end, now);

// The occurrences the grid shows, as getProjectedRows gives them for a week.
export const getProjectedMonthRows = (occurrences, grid, now = new Date()) =>
  getProjectedRangeRows(occurrences, grid.start, grid.end, now);

const toCell = (acute, planned) => ({
  acute,
  planned,
  count: acute.length + planned.length,
  ...getEstimateTotal([...acute, ...planned]),
});

/*
 * The grid's rows as cells, as { late, weeks, monthTotals }.
 *
 * Each day is { date, inMonth, acute, planned, count, estimatedHours, withoutEstimateCount }, akut
 * and planned each sorted as the week sorts them. Each week carries the same sums over its seven
 * days. late holds the carried rows, which have no day on the grid, with the same sums.
 *
 * monthTotals is { acute, planned, estimatedHours, withoutEstimateCount } over the month's own
 * days and late, which is what the month's name above the grid promises. The days the grid shows
 * from the months either side are left out of it. They are counted in their own month, and in
 * the sums of the week they are in.
 */
export const getMonthCells = (rows, grid) => {
  const sorted = splitAcuteAndPlanned(rows);
  const acute = splitRowsByDays(sorted.acute, grid.days);
  const planned = splitRowsByDays(sorted.planned, grid.days);

  const weeks = grid.weeks.map((week, weekIndex) => {
    const days = week.days.map((date, dayIndex) => {
      const index = weekIndex * DAYS_IN_WEEK + dayIndex;
      return {
        date,
        inMonth: isSameMonth(date, grid.monthStart),
        ...toCell(acute.days[index], planned.days[index]),
      };
    });
    return {
      ...week,
      ...toCell(days.flatMap((day) => day.acute), days.flatMap((day) => day.planned)),
      days,
    };
  });

  const late = toCell(acute.late, planned.late);
  const inMonth = weeks.flatMap((week) => week.days).filter((day) => day.inMonth);
  const monthAcute = [...late.acute, ...inMonth.flatMap((day) => day.acute)];
  const monthPlanned = [...late.planned, ...inMonth.flatMap((day) => day.planned)];

  return {
    late,
    weeks,
    monthTotals: {
      acute: monthAcute.length,
      planned: monthPlanned.length,
      ...getEstimateTotal([...monthAcute, ...monthPlanned]),
    },
  };
};
